// ---------------------------------------------------------------------------
// Application entry point.
//
// Responsibilities (in order):
//   1. Validate runtime config (config.js's requireEnv throws at module load
//      if a required env var is missing).
//   2. Configure Amplify against the Cognito User Pool.
//   3. Install global unhandled-rejection / error logging — these complement
//      the React ErrorBoundary (which catches render-phase errors) by
//      catching async/Promise errors that React does not see.
//   4. Mount the React tree under ErrorBoundary + QueryClientProvider.
// ---------------------------------------------------------------------------

import React from 'react';
import ReactDOM from 'react-dom/client';
import { Amplify } from 'aws-amplify';
import { QueryClient, QueryClientProvider } from 'react-query';
import { COGNITO_CONFIG } from './config';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { VersionUpdateBanner } from './components/VersionUpdateBanner';
import './index.css';

// Build id baked in by the versionManifest() plugin in vite.config.mjs (the
// short git SHA in a real build, 'dev' in the dev server). The `typeof` guard
// keeps this a safe read if the define ever fails to apply — falls back to a
// non-deploy id so the banner simply disables itself.
const APP_VERSION = typeof __APP_VERSION__ === 'undefined' ? 'dev' : __APP_VERSION__;

// 1. Config validated by importing it (requireEnv throws on missing values).

// 2. Configure Amplify. We pass only the Cognito identity-provider settings;
//    the Dev Portal does not use Amplify Storage / API / DataStore.
Amplify.configure({
  Auth: {
    Cognito: {
      region: COGNITO_CONFIG.region,
      userPoolId: COGNITO_CONFIG.userPoolId,
      userPoolClientId: COGNITO_CONFIG.userPoolClientId,
    },
  },
});

// 3. Global async error logging. Render-phase errors are caught by
//    ErrorBoundary; this covers everything else (rejected Promises from
//    fetch calls, errors thrown inside event handlers that don't bubble,
//    setTimeout/setInterval callback failures).
window.addEventListener('unhandledrejection', (event) => {
  console.error('[dev-portal] unhandled promise rejection', event.reason);
});
window.addEventListener('error', (event) => {
  console.error('[dev-portal] uncaught error', event.error ?? event.message);
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      {/*
        App-wide, route-independent: polls version.json and offers a
        user-initiated refresh when a newer build is deployed, so a long-open
        tab never strands on a stale shell (a pruned lazy chunk would otherwise
        404). The reload is never forced.
      */}
      <VersionUpdateBanner currentVersion={APP_VERSION} />
      <App />
    </QueryClientProvider>
  </ErrorBoundary>
);
